import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import github from "../../context/github/githubApi";
import Spinner from "../layout/spinner";
import RepoCard from "../repos/RepoCard";
import Pagination from "../ui/Pagination";
import InsightsPanel from "../home/InsightsPanel";
import NotFound from "../pages/NotFound";
import { buildProfileReposQuery } from "../../utils/githubSearchQueries";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { compactNumber } from "../../utils/relativeTime";
import {
  IconUser,
  IconMail,
  IconBuilding,
  IconMap,
  IconCalendar,
  IconGlobe,
  IconPhone,
  IconGitHub,
  IconLink,
} from "./profileIcons";

const fmtJoined = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

const User = () => {
  const { login } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  const [profileLanguages, setProfileLanguages] = useState(null);
  const [insightsBundle, setInsightsBundle] = useState(null);

  const [repoItems, setRepoItems] = useState([]);
  const [repoTotal, setRepoTotal] = useState(0);
  const [repoPage, setRepoPage] = useState(1);
  const [repoLoading, setRepoLoading] = useState(false);
  const [pf, setPf] = useState({
    name: "",
    language: "",
    sortRepo: "stars",
  });

  const [copied, setCopied] = useState(false);
  const [repoView, setRepoView] = useState("grid");
  const [profileAsideOpen, setProfileAsideOpen] = useState(false);
  const debouncedName = useDebouncedValue(pf.name, 450);

  useEffect(() => {
    setProfileAsideOpen(false);
  }, [login]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 992px)");
    const close = () => {
      if (mq.matches) setProfileAsideOpen(false);
    };
    mq.addEventListener("change", close);
    close();
    return () => mq.removeEventListener("change", close);
  }, []);

  useEffect(() => {
    if (!profileAsideOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setProfileAsideOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [profileAsideOpen]);

  useEffect(() => {
    if (!profileAsideOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [profileAsideOpen]);

  useEffect(() => {
    if (!login) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    github
      .get(`/users/${encodeURIComponent(login)}`)
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch(() => {
        if (!cancelled) setLoadErr("User not found.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [login]);

  useEffect(() => {
    if (!login || !user) return;
    let cancelled = false;
    setProfileLanguages(null);
    setInsightsBundle(null);

    const loadReposMeta = async () => {
      const langs = new Set();
      const languageCounts = {};
      let totalStars = 0;
      let totalRepoCount = 0;
      let topRepo = null;
      let page = 1;
      const perPage = 100;
      /** Fewer pages + small delay avoids GitHub abuse / secondary rate limits (token still helps). */
      const maxPages = 5;
      const delayMs = 220;
      const delay = (ms) => new Promise((r) => setTimeout(r, ms));
      try {
        while (page <= maxPages) {
          if (page > 1) await delay(delayMs);
          const res = await github.get(`/users/${encodeURIComponent(login)}/repos`, {
            params: {
              per_page: perPage,
              page,
              sort: "pushed",
              direction: "desc",
            },
          });
          const items = res.data || [];
          if (items.length === 0) break;
          totalRepoCount += items.length;
          for (const r of items) {
            totalStars += r.stargazers_count || 0;
            const lang = r.language;
            if (lang) {
              langs.add(lang);
              languageCounts[lang] = (languageCounts[lang] || 0) + 1;
            }
            if (
              !topRepo ||
              (r.stargazers_count || 0) > (topRepo.stargazers_count || 0)
            ) {
              topRepo = r;
            }
          }
          if (items.length < perPage) break;
          page += 1;
        }
      } catch {
        /* partial data ok */
      }
      if (!cancelled) {
        setProfileLanguages(Array.from(langs).sort((a, b) => a.localeCompare(b)));
        setInsightsBundle({
          languages: languageCounts,
          totalStars,
          repoCount: totalRepoCount,
          topRepo: topRepo
            ? {
                name: topRepo.name,
                html_url: topRepo.html_url,
                stargazers_count: topRepo.stargazers_count,
                description: topRepo.description,
              }
            : null,
          contributionHint: "",
        });
      }
    };

    loadReposMeta();
    return () => {
      cancelled = true;
    };
  }, [login, user]);

  useEffect(() => {
    if (profileLanguages === null) return;
    if (pf.language && !profileLanguages.includes(pf.language)) {
      setPf((p) => ({ ...p, language: "" }));
    }
  }, [profileLanguages, pf.language]);

  useEffect(() => {
    setRepoPage(1);
  }, [login, debouncedName, pf.language]);

  const fetchRepos = useCallback(async () => {
    if (!login || !user) return;
    setRepoLoading(true);
    try {
      const q = buildProfileReposQuery(login, debouncedName, {
        language: pf.language,
      });
      const sort = pf.sortRepo === "updated" ? "updated" : "stars";
      const res = await github.get("/search/repositories", {
        params: {
          q,
          page: repoPage,
          per_page: 12,
          sort,
          order: "desc",
        },
      });
      setRepoItems(res.data.items || []);
      setRepoTotal(res.data.total_count || 0);
    } catch {
      setRepoItems([]);
      setRepoTotal(0);
    } finally {
      setRepoLoading(false);
    }
  }, [login, user, debouncedName, pf.language, pf.sortRepo, repoPage]);

  useEffect(() => {
    fetchRepos();
  }, [fetchRepos]);

  useEffect(() => {
    if (user?.login) {
      document.title = `${user.login} · GitHub Finder`;
    }
  }, [user]);

  const copyProfileLink = () => {
    const url = `${window.location.origin}/user/${login}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="gf-user-page gf-user-dashboard">
        <Spinner />
      </div>
    );
  }

  if (loadErr || !user) {
    return (
      <NotFound
        code="404"
        title="User not found"
        lead={`No GitHub profile matches “${login}”. The account may not exist, or it was renamed or removed.`}
        documentTitle={`User not found · GitHub Finder`}
      />
    );
  }

  const {
    name,
    avatar_url,
    location,
    bio,
    company,
    blog,
    html_url,
    email,
    followers,
    following,
    public_repos,
    public_gists,
    hireable,
    created_at: createdAt,
  } = user;

  const blogUrl = blog?.trim()
    ? blog.startsWith("http")
      ? blog
      : `https://${blog}`
    : null;

  const displayName = name?.trim() || login;
  const showHireableBadge = hireable === true;

  const perPage = 12;

  return (
    <div className="gf-user-page gf-user-dashboard gf-user-dashboard--profile-layout">
      <div className="gf-user-dashboard__split gf-user-dashboard__split--profile">
        {profileAsideOpen && (
          <button
            type="button"
            className="gf-user-profile-sidebar-backdrop"
            aria-label="Close profile panel"
            onClick={() => setProfileAsideOpen(false)}
          />
        )}
        <aside
          id="gf-user-profile-sidebar"
          className={`gf-user-profile-sidebar${profileAsideOpen ? " is-open" : ""}`}
          aria-label="Profile"
        >
          <div className="gf-user-profile-sidebar__mobile-bar">
            <span className="gf-user-profile-sidebar__mobile-title">Profile</span>
            <button
              type="button"
              className="gf-user-profile-sidebar__close"
              onClick={() => setProfileAsideOpen(false)}
              aria-label="Close profile"
            >
              ✕
            </button>
          </div>
          <div className="gf-user-profile-card">
            <section className="gf-user-profile-card__section gf-user-profile-card__section--header">
              <img
                src={avatar_url}
                alt=""
                className="gf-user-profile-card__avatar"
                width={120}
                height={120}
              />
              <div className="gf-user-profile-card__name-row">
                <h1 className="gf-user-profile-card__name">{displayName}</h1>
                <span className="gf-user-profile-card__verified" title="GitHub user" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="var(--gf-primary)"
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                    />
                  </svg>
                </span>
              </div>
              <p className="gf-user-profile-card__handle">@{login}</p>
              <div className="gf-user-profile-card__badges">
                <span
                  className={
                    showHireableBadge
                      ? "gf-user-dashboard__badge gf-user-dashboard__badge--hire-yes"
                      : "gf-user-dashboard__badge gf-user-dashboard__badge--hire-no"
                  }
                >
                  {!showHireableBadge && (
                    <span className="gf-user-dashboard__badge-x" aria-hidden="true">
                      ✕
                    </span>
                  )}
                  {showHireableBadge ? "Available for Hire" : "Not Available for Hire"}
                </span>
              </div>
              <p className="gf-user-profile-card__source-hint gf-muted">
                Public data from GitHub
                <span className="gf-user-profile-card__source-tip" title="Profile fields come from the public GitHub API.">
                  {" "}
                  ⓘ
                </span>
              </p>
            </section>

            <section className="gf-user-profile-card__section" aria-label="Profile statistics">
              <div className="gf-user-profile-stats-strip">
                <div className="gf-user-profile-stat">
                  <span className="gf-user-profile-stat__ico" aria-hidden="true">
                    ⭐
                  </span>
                  <span className="gf-user-profile-stat__num">{compactNumber(followers ?? 0)}</span>
                  <span className="gf-user-profile-stat__lbl">Followers</span>
                </div>
                <div className="gf-user-profile-stat">
                  <span className="gf-user-profile-stat__ico" aria-hidden="true">
                    👤
                  </span>
                  <span className="gf-user-profile-stat__num">{compactNumber(following ?? 0)}</span>
                  <span className="gf-user-profile-stat__lbl">Following</span>
                </div>
                <div className="gf-user-profile-stat">
                  <span className="gf-user-profile-stat__ico" aria-hidden="true">
                    📦
                  </span>
                  <span className="gf-user-profile-stat__num">{public_repos ?? 0}</span>
                  <span className="gf-user-profile-stat__lbl">Repositories</span>
                </div>
              </div>
              <p className="gf-user-profile-card__gist-note gf-muted">
                <span aria-hidden="true">📎</span> {public_gists ?? 0} public gists
              </p>
            </section>

            {bio?.trim() && (
              <section className="gf-user-profile-card__section gf-user-profile-card__section--about">
                <h2 className="gf-user-profile-card__about-title">About</h2>
                <p className="gf-user-profile-card__bio">{bio.trim()}</p>
              </section>
            )}

            <section className="gf-user-profile-card__section gf-user-profile-card__section--meta">
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconMap />
                  <span className="gf-user-profile-detail__label">Location</span>
                </div>
                <span className="gf-user-profile-detail__val">{location?.trim() || "—"}</span>
              </div>
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconBuilding />
                  <span className="gf-user-profile-detail__label">Company</span>
                </div>
                <span
                  className={`gf-user-profile-detail__val${company?.trim() ? " gf-user-dashboard__detail-val--company" : ""}`}
                >
                  {company?.trim() || "—"}
                </span>
              </div>
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconGlobe />
                  <span className="gf-user-profile-detail__label">Website</span>
                </div>
                <span className="gf-user-profile-detail__val">
                  {blogUrl ? (
                    <a href={blogUrl} target="_blank" rel="noopener noreferrer" className="gf-user-dashboard__link">
                      {(blog || "").length > 22 ? `${(blog || "").slice(0, 22)}…` : blog || ""}
                    </a>
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconMail />
                  <span className="gf-user-profile-detail__label">Email</span>
                </div>
                <span className="gf-user-profile-detail__val gf-user-profile-detail__val--muted">
                  {email ? (
                    <a href={`mailto:${email}`} className="gf-user-dashboard__link">
                      {email}
                    </a>
                  ) : (
                    "Not found"
                  )}
                </span>
              </div>
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconPhone />
                  <span className="gf-user-profile-detail__label">Phone</span>
                </div>
                <span className="gf-user-profile-detail__val gf-user-profile-detail__val--muted">—</span>
              </div>
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconCalendar />
                  <span className="gf-user-profile-detail__label">Joined GitHub</span>
                </div>
                <span className="gf-user-profile-detail__val">{fmtJoined(createdAt)}</span>
              </div>
              <div className="gf-user-profile-detail">
                <div className="gf-user-profile-detail__label-wrap">
                  <IconUser />
                  <span className="gf-user-profile-detail__label">Name</span>
                </div>
                <span className="gf-user-profile-detail__val">{displayName}</span>
              </div>
            </section>

            <section className="gf-user-profile-card__section gf-user-profile-card__section--actions">
              <a
                href={html_url}
                className="gf-btn gf-btn--primary gf-btn--block gf-user-profile-btn gf-user-profile-btn--github"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="gf-user-profile-btn__left">
                  <IconGitHub />
                  Open on GitHub
                </span>
                <span className="gf-user-profile-btn__ext" aria-hidden="true">
                  ↗
                </span>
              </a>
              <button
                type="button"
                className="gf-btn gf-btn--outline gf-btn--block gf-user-profile-btn gf-user-profile-btn--copy"
                onClick={copyProfileLink}
              >
                <IconLink />
                {copied ? "Copied!" : "Copy profile link"}
              </button>
            </section>
          </div>

          <div className="gf-user-profile-insights">
            <InsightsPanel
              login={login}
              data={insightsBundle}
              compact
              heading="Top languages"
              hideHint
              statsVariant="profile"
              fillVertical={false}
            />
          </div>
        </aside>

        <div className="gf-user-profile-main">
          <section className="gf-card gf-user-repos-section gf-user-repos-section--main">
            <header className="gf-user-repos-header">
              <h2 className="gf-user-repos-section__title">Repositories</h2>
              <p className="gf-user-repos-header__sub gf-muted">
                {public_repos ?? 0} public {public_repos === 1 ? "repository" : "repositories"}
                {repoTotal > 0 && (
                  <span className="gf-user-repos-header__hint">
                    {" "}
                    · {repoTotal.toLocaleString()} match{repoTotal === 1 ? "" : "es"} current filters
                  </span>
                )}
              </p>
            </header>

            <div className="gf-user-repo-toolbar">
              <div className="gf-user-repo-filters gf-user-repo-filters--toolbar">
                <label className="gf-label gf-user-repo-filters__field gf-user-repo-filters__field--grow">
                  <span className="gf-user-repo-filters__label">Search repositories</span>
                  <input
                    type="search"
                    className="gf-input gf-user-repo-filters__control"
                    value={pf.name}
                    onChange={(e) => setPf((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Search repositories…"
                    autoComplete="off"
                  />
                </label>
                <label className="gf-label gf-user-repo-filters__field gf-user-repo-filters__field--lang">
                  <span className="gf-user-repo-filters__label">Language</span>
                  <select
                    className="gf-input gf-select gf-user-repo-filters__control"
                    value={pf.language}
                    onChange={(e) => setPf((p) => ({ ...p, language: e.target.value }))}
                    disabled={profileLanguages === null}
                    aria-busy={profileLanguages === null}
                  >
                    <option value="">All</option>
                    {(profileLanguages || []).map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="gf-label gf-user-repo-filters__field gf-user-repo-filters__field--sort">
                  <span className="gf-user-repo-filters__label">Sort</span>
                  <select
                    className="gf-input gf-select gf-user-repo-filters__control"
                    value={pf.sortRepo}
                    onChange={(e) => setPf((p) => ({ ...p, sortRepo: e.target.value }))}
                  >
                    <option value="stars">Most stars</option>
                    <option value="updated">Recently updated</option>
                  </select>
                </label>
                <div className="gf-label gf-user-repo-filters__field gf-user-repo-filters__field--view">
                  <span className="gf-user-repo-filters__label">View</span>
                  <div className="gf-user-repo-view-toggle" role="group" aria-label="Result layout">
                    <button
                      type="button"
                      className={`gf-user-repo-view-toggle__btn ${repoView === "grid" ? "is-active" : ""}`}
                      onClick={() => setRepoView("grid")}
                      aria-pressed={repoView === "grid"}
                      aria-label="Grid view"
                    >
                      ⊞
                    </button>
                    <button
                      type="button"
                      className={`gf-user-repo-view-toggle__btn ${repoView === "list" ? "is-active" : ""}`}
                      onClick={() => setRepoView("list")}
                      aria-pressed={repoView === "list"}
                      aria-label="List view"
                    >
                      ☰
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {repoLoading && repoItems.length === 0 ? (
              <div className={repoView === "grid" ? "gf-repo-grid gf-repo-grid--profile" : "gf-repo-list"}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="gf-skeleton gf-skeleton--repo-card" />
                ))}
              </div>
            ) : repoItems.length === 0 ? (
              <p className="gf-empty gf-empty--card">No repositories match these filters.</p>
            ) : (
              <>
                <div className={repoView === "grid" ? "gf-repo-grid gf-repo-grid--profile" : "gf-repo-list"}>
                  {repoItems.map((repo) => (
                    <RepoCard
                      key={repo.id}
                      repo={repo}
                      hideOwnerContext
                      variant={repoView}
                    />
                  ))}
                </div>
                <div className="gf-user-repo-footer">
                  <Pagination
                    page={repoPage}
                    total={repoTotal}
                    perPage={perPage}
                    onPageChange={(p) => setRepoPage(p)}
                    disabled={repoLoading}
                    aria-label="Profile repositories pagination"
                  />
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      <button
        type="button"
        className={`gf-user-profile-fab${profileAsideOpen ? " is-hidden" : ""}`}
        onClick={() => setProfileAsideOpen(true)}
        aria-controls="gf-user-profile-sidebar"
        aria-label="Open profile and insights"
      >
        <span className="gf-user-profile-fab__ico" aria-hidden="true">
          ☰
        </span>
        <span className="gf-user-profile-fab__txt">Profile</span>
      </button>
    </div>
  );
};

export default User;
